/**
 * Public-facing strategies marketplace at strategies.axon-trading.com.
 *
 * Static-only site:
 *   - Catalog is fetched once from /v1/catalog.json (synced from
 *     /catalog/catalog.json by prebuild.mjs).
 *   - No backend, no auth, no votes (those land in PR #3 of the
 *     v1.2 plan).
 *   - Install CTA deeplinks to the user's local Axon daemon, or
 *     shows a CLI install path if no daemon is detected.
 *
 * Trust model is the result of PR #0: the green "Verified by Strykr"
 * badge renders ONLY when the catalog row carries
 * `signerVerified: true` AND `kind === "strykr"` — the build script
 * computed that cryptographically. Publisher-set `verified: true`
 * without crypto backing renders as a low-trust dashed pill.
 */
import { useEffect, useState } from "react";
import { Icon } from "./components/Icon.js";
import { MarketplaceGrid } from "./components/MarketplaceGrid.js";
import { StrategyDetailDialog } from "./components/StrategyDetailDialog.js";
import { fetchCatalog } from "./lib/catalog.js";
import { detectDaemon, type DaemonStatus } from "./lib/daemon.js";
import type { Strategy, CatalogIndex } from "./lib/types.js";
import { VersionFooter } from "./components/VersionFooter.js";

/**
 * Phase 13: respect Vite's BASE_URL when constructing the home
 * link, so the same source works at `/` (standalone Vercel deploy)
 * AND `/strategies/` (unified landing-bundled deploy at
 * axon-trading.com/strategies/).
 *
 * Untyped cast mirrors the workaround in lib/marketplace-api.ts —
 * the marketplace-web tsconfig doesn't reference vite/client types.
 */
function baseUrl(): string {
  return (import.meta as unknown as { env?: { BASE_URL?: string } }).env?.BASE_URL ?? "/";
}

type LoadState =
  | { kind: "loading" }
  | { kind: "ready"; index: CatalogIndex }
  | { kind: "error"; message: string };

export function App() {
  const [load, setLoad] = useState<LoadState>({ kind: "loading" });
  const [daemon, setDaemon] = useState<DaemonStatus | null>(null);
  const [selected, setSelected] = useState<Strategy | null>(null);

  // 1. Fetch the catalog once on mount. Static asset — no need to
  //    refetch on focus / interval. PR #3 swaps this for a polling
  //    query when votes start changing the wire shape.
  useEffect(() => {
    let cancelled = false;
    fetchCatalog()
      .then((index) => {
        if (cancelled) return;
        setLoad({ kind: "ready", index });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : String(err);
        setLoad({ kind: "error", message });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // 2. Probe the local daemon once on mount + every 30s thereafter.
  //    The 30s interval picks up the user starting `axon` mid-session
  //    so the install CTA flips from "install CLI" to "open dashboard"
  //    without requiring a refresh. Probe is no-cors and cheap.
  useEffect(() => {
    let cancelled = false;
    const probe = async () => {
      const status = await detectDaemon();
      if (!cancelled) setDaemon(status);
    };
    void probe();
    const interval = setInterval(probe, 30_000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  return (
    <div className="mw-app">
      <Header daemon={daemon} catalogStrategies={load.kind === "ready" ? load.index.strategies.length : null} />

      <main className="mw-main">
        {load.kind === "loading" && (
          <div className="mw-state">
            <span className="mw-spin" />
            <span>Loading catalog…</span>
          </div>
        )}
        {load.kind === "error" && (
          <div className="mw-state mw-error">
            <Icon name="alert" size={18} />
            <div>
              <strong>Catalog unavailable.</strong>
              <div className="mw-error-msg">{load.message}</div>
            </div>
          </div>
        )}
        {load.kind === "ready" && (
          <MarketplaceGrid catalog={load.index.strategies} onOpen={setSelected} />
        )}
      </main>

      <StrategyDetailDialog
        open={!!selected}
        strategy={selected}
        daemon={daemon}
        onClose={() => setSelected(null)}
      />

      <Footer />
      <Styles />
    </div>
  );
}

/* -------------------- header -------------------- */

function Header({
  daemon,
  catalogStrategies,
}: {
  daemon: DaemonStatus | null;
  catalogStrategies: number | null;
}) {
  return (
    <header className="mw-header">
      <div className="mw-header-inner">
        <a href={baseUrl()} className="mw-brand" aria-label="Axon Strategies marketplace home">
          <span className="mw-brand-mark">A</span>
          <div className="mw-brand-text">
            <span className="mw-brand-name">Axon Strategies</span>
            <span className="mw-brand-tag">Verified marketplace for AI trading agents</span>
          </div>
        </a>
        <div className="mw-header-meta">
          <DaemonPill daemon={daemon} />
          <a
            href="https://axonterminal.ai"
            target="_blank"
            rel="noopener noreferrer"
            className="mw-header-link"
          >
            <Icon name="external" size={11} />
            axonterminal.ai
          </a>
        </div>
      </div>
      <div className="mw-subhead">
        <h1>
          {catalogStrategies !== null
            ? `${catalogStrategies} strategy${catalogStrategies === 1 ? "" : "s"} available`
            : "Strategy marketplace"}
        </h1>
        <p>
          Signed, verified strategies for autonomous trading agents. Browse here, install into
          your local <code className="mw-code">axon</code> daemon. Non-custodial — keys never
          leave your machine.
        </p>
      </div>
    </header>
  );
}

function DaemonPill({ daemon }: { daemon: DaemonStatus | null }) {
  if (daemon === null) {
    return (
      <span className="mw-pill mw-pill-probe">
        <span className="mw-spin small" /> probing local daemon
      </span>
    );
  }
  if (daemon.reachable) {
    return (
      <span className="mw-pill mw-pill-ok">
        <span className="mw-pill-dot" /> local daemon detected
      </span>
    );
  }
  return (
    <span className="mw-pill mw-pill-off" title="No Axon daemon running on 127.0.0.1:54321 — install instructions appear when you click any strategy.">
      <span className="mw-pill-dot" /> no local daemon
    </span>
  );
}

/* -------------------- footer -------------------- */

function Footer() {
  return (
    <footer className="mw-footer">
      <div className="mw-footer-inner">
        <span>
          Strategies are signed code, not investment advice. Past performance doesn't predict future results.
        </span>
        <span className="mw-footer-links">
          <a href="https://axonterminal.ai" target="_blank" rel="noopener noreferrer">
            ← axonterminal.ai
          </a>
          <span className="mw-footer-sep">·</span>
          <a
            href="https://www.npmjs.com/package/@axon-trading/cli"
            target="_blank"
            rel="noopener noreferrer"
          >
            Install the CLI
          </a>
          <span className="mw-footer-sep">·</span>
          <VersionFooter />
        </span>
      </div>
    </footer>
  );
}

/* -------------------- styles -------------------- */

function Styles() {
  return (
    <style>{`
      .mw-app { min-height: 100vh; display: flex; flex-direction: column; }

      /* header */
      .mw-header { border-bottom: 1px solid var(--at-border);
        background: linear-gradient(180deg, var(--at-surface), transparent); }
      .mw-header-inner { display: flex; justify-content: space-between; align-items: center;
        max-width: 1280px; margin: 0 auto; padding: 16px 20px; gap: 16px; flex-wrap: wrap; }
      .mw-brand { display: flex; align-items: center; gap: 12px; color: inherit; }
      .mw-brand-mark { width: 32px; height: 32px; border-radius: 8px;
        background: var(--at-accent); color: var(--at-bg);
        display: flex; align-items: center; justify-content: center;
        font-family: var(--at-font-mono); font-weight: 700; font-size: 16px; }
      .mw-brand-text { display: flex; flex-direction: column; gap: 1px; }
      .mw-brand-name { font-size: 14px; font-weight: 600; color: var(--at-fg); letter-spacing: -0.2px; }
      .mw-brand-tag { font-family: var(--at-font-mono); font-size: 10px;
        letter-spacing: 0.05em; color: var(--at-muted); }
      @media (max-width: 600px) { .mw-brand-tag { display: none; } }

      .mw-header-meta { display: flex; align-items: center; gap: 10px; }
      .mw-header-link { display: inline-flex; align-items: center; gap: 5px;
        font-family: var(--at-font-mono); font-size: 11px; color: var(--at-muted);
        padding: 5px 8px; border-radius: var(--at-radius-sm);
        transition: color var(--at-dur-fast) var(--at-ease); }
      .mw-header-link:hover { color: var(--at-fg); background: var(--at-bg-hover); }

      .mw-pill { display: inline-flex; align-items: center; gap: 6px;
        font-family: var(--at-font-mono); font-size: 10px; letter-spacing: 0.12em;
        text-transform: uppercase; padding: 4px 9px; border-radius: var(--at-radius-full);
        border: 1px solid var(--at-hairline); color: var(--at-muted); cursor: default; }
      .mw-pill-dot { width: 6px; height: 6px; border-radius: 50%;
        background: var(--at-muted); flex-shrink: 0; }
      .mw-pill-ok { color: var(--at-accent); border-color: rgba(74,222,128,0.22);
        background: rgba(74,222,128,0.06); }
      .mw-pill-ok .mw-pill-dot { background: var(--at-accent); box-shadow: 0 0 0 3px rgba(74,222,128,0.15); }
      .mw-pill-off .mw-pill-dot { background: var(--at-muted); }
      .mw-pill-probe { color: var(--at-muted); }
      .mw-spin { width: 10px; height: 10px;
        border: 1.5px solid var(--at-hairline); border-top-color: var(--at-accent);
        border-radius: 50%; animation: mw-spin 0.8s linear infinite; }
      .mw-spin.small { width: 8px; height: 8px; border-width: 1.2px; }
      @keyframes mw-spin { to { transform: rotate(360deg); } }

      .mw-subhead { max-width: 1280px; margin: 0 auto; padding: 0 20px 24px; }
      .mw-subhead h1 { margin: 0 0 6px; font-size: 26px; font-weight: 600;
        color: var(--at-fg); letter-spacing: -0.5px; }
      .mw-subhead p { margin: 0; font-size: 13.5px; color: var(--at-muted);
        line-height: 1.55; max-width: 720px; }
      .mw-code { font-family: var(--at-font-mono); font-size: 12px;
        background: var(--at-surface); padding: 1px 5px;
        border: 1px solid var(--at-hairline); border-radius: var(--at-radius-sm);
        color: var(--at-fg); }

      /* main */
      .mw-main { flex: 1; }
      .mw-state { display: flex; align-items: center; gap: 12px;
        max-width: 720px; margin: 64px auto; padding: 24px;
        font-size: 13px; color: var(--at-muted);
        background: var(--at-surface); border: 1px solid var(--at-hairline);
        border-radius: var(--at-radius-md); }
      .mw-error { color: var(--at-warn); border-color: rgba(251,191,36,0.25);
        background: rgba(251,191,36,0.05); }
      .mw-error strong { display: block; color: var(--at-fg); margin-bottom: 4px; }
      .mw-error-msg { font-family: var(--at-font-mono); font-size: 11px; color: var(--at-muted); }

      /* footer */
      .mw-footer { border-top: 1px solid var(--at-border); margin-top: 32px;
        background: var(--at-bg); }
      .mw-footer-inner { max-width: 1280px; margin: 0 auto; padding: 18px 20px;
        display: flex; justify-content: space-between; gap: 16px;
        font-size: 11.5px; color: var(--at-muted); flex-wrap: wrap; }
      .mw-footer-links { display: inline-flex; align-items: center; gap: 8px; }
      .mw-footer-links a { color: var(--at-muted); }
      .mw-footer-links a:hover { color: var(--at-fg); }
      .mw-footer-sep { opacity: 0.4; }
    `}</style>
  );
}
